import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChatPage } from '../../shared/chat-page/chat-page';
import { LivekitService } from '../../core/services/livekit.service';
import { Dialog } from '@angular/cdk/dialog';
import { DeclinedDialog, DisclaimerService, GuestFormData } from '../../shared/disclaimer';
import { EMPTY, Observable, switchMap, tap } from 'rxjs';
import { SessionPayload } from '../../core/models/livekit.model';

const DISCLAIMER_ACCEPTED = 'MIB_live_disclaimer_accepted';
const AUDIENCE_ROLE = 'audience';

@Component({
  selector: 'app-audience',
  imports: [ChatPage],
  templateUrl: './audience.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Audience implements OnInit {
  private readonly disclaimerService = inject(DisclaimerService);
  private readonly dialogService = inject(Dialog);

  private readonly livekit = inject(LivekitService);
  private readonly destroyRef = inject(DestroyRef);

  readonly hasJoined = signal(false);
  readonly showDisclaimer = signal(true);

  readonly isDisconnected = this.livekit.isDisconnected;

  constructor() {
    this.destroyRef.onDestroy(() => this.livekit.cleanupAfterDisconnection().subscribe());
  }

  ngOnInit(): void {
    const hasStoredSession = this.livekit.hasValidStoredSession(AUDIENCE_ROLE);
    const disclaimerAccepted = sessionStorage.getItem(DISCLAIMER_ACCEPTED);
    if (!hasStoredSession || !disclaimerAccepted) {
      this.openDisclaimer();
    } else {
      this.showDisclaimer.set(false);
      this.connect({ role: AUDIENCE_ROLE }).subscribe();
    }
  }

  private openDisclaimer(): void {
    this.disclaimerService
      .openDisclaimer(true)
      .closed.pipe(
        switchMap((disclaimerData) => {
          if (typeof disclaimerData !== 'object') {
            const cancelRef = this.dialogService.open(DeclinedDialog);
            return cancelRef.closed.pipe(tap(() => this.openDisclaimer()));
          } else {
            this.showDisclaimer.set(false);
            sessionStorage.setItem(DISCLAIMER_ACCEPTED, 'accepted');

            return this.join(disclaimerData);
          }
        }),
      )
      .subscribe({
        complete: () => {
          this.hasJoined.set(true);
        },
      });
  }

  private join(data: GuestFormData): Observable<void> {
    const { email, fullName } = data;
    if (!email || !fullName) return EMPTY;

    this.hasJoined.set(true);
    return this.connect({ role: AUDIENCE_ROLE, participantName: fullName, email }).pipe(
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private connect(payload: SessionPayload): Observable<void> {
    return this.livekit.connect(payload).pipe(takeUntilDestroyed(this.destroyRef));
  }
}
